import React, { useState, useRef, useEffect, useCallback } from 'react';
import MessageBubble from './MessageBubble';
import api from '../services/api';
import logoImage from '../assets/smartagedcare.jpg';
import './ChatInterface.css';

function ChatInterface({ chatState, updateChatState }) {
  // 使用傳入的全域狀態，如果沒有則使用預設值
  const messages = chatState?.messages || [
    { 
      role: 'assistant', 
      content: '您好！我是智慧照顧產品推薦專家，請問您在尋找哪方面的協助或產品呢？' 
    }
  ];
  const conversationId = chatState?.conversationId || null;

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  
  // 推薦按鈕狀態管理
  const [clickedButtons, setClickedButtons] = useState(new Set());
  const [buttonLoading, setButtonLoading] = useState(false);
  
  // 語音輸入狀態
  const [isRecording, setIsRecording] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [voiceStatus, setVoiceStatus] = useState('');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const isActiveRef = useRef(false);



  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 語音識別相關函數
  const startVoiceRecognition = useCallback(async () => {
    if (isActiveRef.current) return;
    
    // 檢查瀏覽器支援
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setVoiceError('您的瀏覽器不支持語音識別功能');
      return;
    }

    try {
      isActiveRef.current = true;
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.lang = 'zh-TW';
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onstart = () => {
        setIsRecording(true);
        setVoiceError('');
        setVoiceStatus('正在聆聽...');
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setVoiceStatus('識別成功！');
        setTimeout(() => setVoiceStatus(''), 2000);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('語音識別錯誤:', event.error);
        setVoiceError('語音識別失敗，請重試');
        setVoiceStatus('');
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
        isActiveRef.current = false;
        if (voiceStatus !== '識別成功！') {
          setVoiceStatus('');
        }
      };

      recognitionRef.current.start();
    } catch (error) {
      console.error('語音識別初始化失敗:', error);
      setVoiceError('語音識別初始化失敗');
      isActiveRef.current = false;
    }
  }, [voiceStatus]);

  const stopVoiceRecognition = useCallback(() => {
    if (recognitionRef.current && isActiveRef.current) {
      recognitionRef.current.stop();
      isActiveRef.current = false;
    }
    setIsRecording(false);
    setVoiceStatus('');
  }, []);

  // 輪詢排名狀態
  const pollRankingStatus = useCallback((newConversationId) => {
    let pollAttempts = 0;
    const maxAttempts = 60; // 延長到 60 次 (約 60 秒)，給後端重試機制更多時間
    
    const pollInterval = setInterval(async () => {
      pollAttempts++;
      
      try {
        const statusResponse = await api.checkRankingStatus(newConversationId);
        
        if (statusResponse.status === 'completed') {
          clearInterval(pollInterval);
          setIsLoading(false);
          
          // ✅ 排名完成後，不發送額外API請求，避免重複調用
          console.log('排名已完成，等待用戶下次互動時自動獲取結果');
          
          // 更新最後一條助手消息為完成狀態，不發送API請求
          const currentMessages = chatState?.messages || messages;
          const newMessages = [...currentMessages];
          
          // 找到最後一條助手消息並替換為完成提示
          for (let i = newMessages.length - 1; i >= 0; i--) {
            if (newMessages[i].role === 'assistant') {
              newMessages[i] = { 
                role: 'assistant', 
                content: '✅ 產品分析已完成！請點選繼續按鈕，我會根據分析結果為您提供精準的產品推薦。',
                showContinueButton: true
              };
              break;
            }
          }
          
          updateChatState({
            messages: newMessages,
            conversationId: newConversationId
          });
        } else if (pollAttempts >= maxAttempts) {
          // 超時處理：不發送額外的API請求，直接顯示超時訊息
          clearInterval(pollInterval);
          setIsLoading(false);
          
          console.log('排名生成超時，顯示超時訊息');
          
          // 顯示超時訊息，不發送額外的API請求
          const timeoutMessage = { 
            role: 'assistant', 
            content: `⏰ 系統分析時間較長，但我們正在後台為您處理。請您：

1. **稍等片刻**：系統可能正在自動重試
2. **重新描述需求**：或重新告訴我您的產品需求  
3. **點擊新推薦**：使用右上角「新推薦」重新開始

我會立即為您提供準確的產品推薦！` 
          };
          const currentMessages = chatState?.messages || messages;
          
          // 更新最後一條助手消息而不是添加新消息
          const newMessages = [...currentMessages];
          for (let i = newMessages.length - 1; i >= 0; i--) {
            if (newMessages[i].role === 'assistant') {
              newMessages[i] = timeoutMessage;
              break;
            }
          }
          
          updateChatState({
            messages: newMessages,
            conversationId: newConversationId
          });
        }
      } catch (error) {
        console.error('檢查狀態失敗:', error);
        if (pollAttempts >= maxAttempts) {
          clearInterval(pollInterval);
          setIsLoading(false);
        }
      }
    }, 1000); // 每秒檢查一次
  }, [chatState, messages, updateChatState]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { role: 'user', content: inputValue };
    const newMessages = [...messages, userMessage];
    
    // 更新全域狀態
    updateChatState({
      messages: newMessages,
      conversationId: conversationId
    });
    
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await api.sendMessage(conversationId, userMessage.content);
      
      if (response.data && response.data.reply) {
        const assistantMessage = { role: 'assistant', content: response.data.reply };
        const updatedMessages = [...newMessages, assistantMessage];
        
        // 更新全域狀態，包含新的conversationId
        const newConversationId = response.data.conversationId || conversationId;
        updateChatState({
          messages: updatedMessages,
          conversationId: newConversationId
        });

        // 檢查是否為分析中狀態，如果是則開始輪詢
        if (response.data.status === 'analyzing') {
          pollRankingStatus(newConversationId);
        }
      }
    } catch (error) {
      console.error('發送訊息失敗:', error);
      
      // 提取具體的錯誤信息
      let errorContent = '抱歉，發生了錯誤。請重試。';
      
      if (error.response) {
        // 後端返回的錯誤響應
        if (error.response.data && error.response.data.error) {
          errorContent = error.response.data.error;
          console.log('後端錯誤詳情:', error.response.data.details);
        } else if (error.response.status === 500) {
          errorContent = '系統暫時無法處理您的請求，請稍後再試。';
        } else if (error.response.status === 401) {
          errorContent = 'API認證失敗，請聯絡系統管理員。';
        }
      } else if (error.request) {
        // 網路連接問題
        errorContent = '網路連接失敗，請檢查網路連接後重試。';
      } else if (error.code === 'ECONNABORTED') {
        // 請求超時
        errorContent = '請求超時，請重新嘗試。';
      }
      
      const errorMessage = { role: 'assistant', content: errorContent };
      updateChatState({
        messages: [...newMessages, errorMessage],
        conversationId: conversationId
      });
    } finally {
      setIsLoading(false);
      // 自動聚焦到輸入欄
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 處理推薦按鈕點擊
  const handleRecommendationAction = async (action, buttonId) => {
    // 檢查是否已經點擊過這個按鈕
    if (clickedButtons.has(buttonId)) {
      // 滾動到對應的回覆
      const targetMessage = messages.find((msg, index) => 
        msg.role === 'assistant' && msg.isRecommendationResult && msg.buttonId === buttonId
      );
      if (targetMessage) {
        // 使用 setTimeout 確保 DOM 已更新
        setTimeout(() => {
          const messageElement = document.querySelector(`[data-message-id="${buttonId}"]`);
          if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
      return;
    }

    if (buttonLoading) return;

    setButtonLoading(true);
    
    try {
      let apiMessage = '';
      if (action === 'continue') {
        apiMessage = '請為我推薦產品';
      } else if (action === 'next_batch') {
        apiMessage = '請為我推薦其他產品';
      }

      const response = await api.sendMessage(conversationId, apiMessage);
      
      if (response.data && response.data.reply) {
        const assistantMessage = { 
          role: 'assistant', 
          content: response.data.reply,
          isRecommendationResult: true,
          buttonId: buttonId,
          showNextBatchButton: action === 'continue' // 只有第一次推薦後才顯示換一批按鈕
        };
        
        const newMessages = [...messages, assistantMessage];
        
        // 標記按鈕為已點擊
        setClickedButtons(prev => new Set([...prev, buttonId]));
        
        updateChatState({
          messages: newMessages,
          conversationId: response.data.conversationId || conversationId
        });
      }
    } catch (error) {
      console.error('推薦請求失敗:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: '抱歉，獲取推薦時發生錯誤，請重試。' 
      };
      updateChatState({
        messages: [...messages, errorMessage],
        conversationId: conversationId
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleNewChat = () => {
    // 重置全域聊天狀態
    const initialState = {
      messages: [
        { 
          role: 'assistant', 
          content: '您好！我是智慧照顧產品推薦專家，請問您在尋找哪方面的協助或產品呢？' 
        }
      ],
      conversationId: null
    };
    updateChatState(initialState);
    
    setInputValue('');
    setIsLoading(false);
    // 重置推薦按鈕狀態
    setClickedButtons(new Set());
    setButtonLoading(false);
    // 重置語音相關狀態
    setIsRecording(false);
    setVoiceError('');
    setVoiceStatus('');
    if (recognitionRef.current && isActiveRef.current) {
      recognitionRef.current.stop();
      isActiveRef.current = false;
    }
    
    // 清除localStorage中的聊天狀態
    localStorage.removeItem('chatState');
  };

  const handleExport = async () => {
    if (!emailAddress) {
      alert('請輸入電子郵件地址');
      return;
    }

    setExportLoading(true);
    try {
      // 🔥 改進：準備對話數據，添加時間戳記以幫助識別最新內容
      const conversationData = messages.map((msg, index) => {
        const role = msg.role === 'user' ? '用戶' : 'AI';
        const timestamp = `[對話第${index + 1}輪]`;
        return `${timestamp} ${role}：${msg.content}`;
      }).join('\n\n');

      // 🔥 改進：添加明確的最新推薦標記
      const enhancedConversationData = conversationData + 
        '\n\n===== 注意：請以上述對話中最後的產品推薦為準 =====';

      const response = await api.exportConversation({
        email: emailAddress,
        conversation: enhancedConversationData,
        conversationId: conversationId // 🔥 新增：傳送conversationId
      });

      if (response.data && response.data.message) {
        alert(response.data.message);
        setShowExportModal(false);
        setEmailAddress('');
      }
    } catch (error) {
      console.error('匯出失敗:', error);
      alert('匯出失敗，請重試');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="chat-interface">
      {/* 頂部區域 */}
      <div className="chat-header">
        <div className="header-content">
          <div className="header-title-section">
            <img src={logoImage} alt="智慧照顧" className="logo" />
            <h1>智慧照顧產品推薦系統</h1>
          </div>
          <div className="header-actions">
            <button 
              onClick={() => setShowExportModal(true)}
              className="export-button"
              disabled={messages.length <= 1}
            >
              <span>📤</span>
              匯出推薦
            </button>
            <button 
              onClick={handleNewChat}
              className="new-chat-button"
            >
              <span>✏️</span>
              新推薦
            </button>
            <button 
              onClick={() => setShowDisclaimerModal(true)}
              className="disclaimer-button"
            >
              <span>ℹ️</span>
              免責聲明
            </button>
          </div>
        </div>
      </div>

      {/* 聊天內容區域 */}
      <div className="chat-body">
        <div className="chat-messages">
          {messages.map((message, index) => (
            <div key={index} data-message-id={message.buttonId}>
              <MessageBubble 
                message={message} 
                isUser={message.role === 'user'} 
              />
              
              {/* 顯示繼續推薦按鈕 */}
              {message.showContinueButton && (
                <div className="action-buttons">
                  <button 
                    className="continue-button"
                    onClick={() => handleRecommendationAction('continue', `continue_${index}`)}
                    disabled={buttonLoading}
                  >
                    {buttonLoading ? '處理中...' : '🔍 繼續推薦'}
                  </button>
                </div>
              )}
              
              {/* 顯示換一批產品按鈕 */}
              {message.showNextBatchButton && (
                <div className="action-buttons">
                  <button 
                    className="next-batch-button"
                    onClick={() => handleRecommendationAction('next_batch', `next_${index}`)}
                    disabled={buttonLoading}
                  >
                    {buttonLoading ? '處理中...' : '🔄 換一批產品'}
                  </button>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="loading-container">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          

          
          {/* 語音狀態顯示 */}
          {voiceStatus && (
            <div className="voice-status">
              <div className="voice-status-content">
                {voiceStatus}
              </div>
            </div>
          )}
          
          {/* 語音錯誤顯示 */}
          {voiceError && (
            <div className="voice-error">
              <div className="voice-error-content">
                {voiceError}
              </div>
            </div>
          )}

          {/* 載入指示器 */}
          {isLoading && (
            <div className="loading-indicator">
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
        <div ref={messagesEndRef} />
        </div>
      </div>

      {/* 輸入區域 */}
      <div className="chat-input">
        <div className="input-container">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="請輸入您的問題..."
            disabled={isLoading}
          />
          <div className="input-actions">
            <button
              onClick={isRecording ? stopVoiceRecognition : startVoiceRecognition}
              className={`voice-button ${isRecording ? 'recording' : ''}`}
              disabled={isLoading}
            >
              {isRecording ? '🔴' : '🎤'}
            </button>
          </div>
        </div>
      </div>

      {/* 匯出模態框 */}
      {showExportModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>匯出聊天記錄</h3>
            <p>請輸入您的電子郵件地址，我們將把聊天記錄發送給您：</p>
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="your.email@example.com"
              className="email-input"
            />
            <div className="modal-actions">
              <button 
                onClick={handleExport} 
                disabled={exportLoading}
                className="export-confirm-button"
              >
                {exportLoading ? '發送中...' : '發送'}
              </button>
              <button 
                onClick={() => setShowExportModal(false)}
                className="modal-cancel-button"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 免責聲明模態框 */}
      {showDisclaimerModal && (
        <div className="modal-overlay">
          <div className="modal-content disclaimer-modal">
            <h3>免責聲明</h3>
            <div className="disclaimer-content">
              <p><strong>系統說明：</strong></p>
              <p>本智慧照顧產品推薦系統採用ChatGPT作為系統邏輯運算核心，透過大型語言模型(LLM)技術提供智慧化產品推薦服務。</p>
              
              <p><strong>產品資訊來源：</strong></p>
              <ul>
                <li>本系統所提供之產品資訊來源於產品官方網站的公開資料</li>
                <li>產品資料係透過人為設計的固定提示詞(prompt)，並使用Perplexity作為AI搜尋引擎進行資料收集</li>
                <li>產品規格、價格及供應狀況可能隨時變動</li>
                <li>建議您在使用推薦結果前，直接聯繫廠商確認最新資訊</li>
              </ul>
              
              <p><strong>推薦流程說明：</strong></p>
              <ol>
                <li>系統會根據您輸入的需求進行智能分析</li>
                <li>透過大型語言模型(LLM)進行產品適配與匹配</li>
                <li>提供詳細的產品比較及推薦理由</li>
                <li>您可透過匯出功能獲取完整的推薦報告</li>
              </ol>
              
              <p><strong>重要提醒：</strong></p>
              <ul>
                <li>本系統僅供參考，不構成購買建議</li>
                <li>最終購買決定應基於您的實際需求及預算考量</li>
                <li>產品品質、售後服務等細節請直接向廠商確認</li>
                <li>本系統不承擔因使用推薦結果而產生的任何責任</li>
              </ul>
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => setShowDisclaimerModal(false)}
                className="modal-cancel-button"
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatInterface; 